// API Security · Proposals — shared Accept / Defer / Reject / Reopen actions.

import React from "react";
import PropTypes from "prop-types";
import { Stack, Button } from "@mui/material";
import { Check, X, Clock, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import colors from "../../../../../../utils/colors";
import LayerdIcon from "../../../../../common/LayerdIcon";
import { GhostButton } from "../../../../../common/Button";
import {
  asecBtnPrimaryIconSm,
  asecBtnOutlinedIconSm,
  asecBtnDangerOutlinedSm,
} from "../asecUi";

function ResolvedFlag({ state }) {
  const conf = state === "accepted"
    ? { kind: "ok", Icon: CheckCircle2, label: "Accepted — promoting on next tick" }
    : state === "rejected"
      ? { kind: "default", Icon: XCircle, label: "Rejected — 30-day backoff" }
      : { kind: "info", Icon: Clock, label: "Deferred — snoozed" };
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{
      fontSize: 12, fontWeight: 600, px: 1.5, py: 0.75, borderRadius: "8px",
      backgroundColor: conf.kind === "ok" ? "#F4FBF6" : conf.kind === "info" ? colors.primary[100] : colors.neutral[50],
      color: conf.kind === "ok" ? colors.green[300] : conf.kind === "info" ? colors.primary[400] : colors.neutral[700],
      border: `1px solid ${conf.kind === "ok" ? "rgba(31,193,107,0.30)" : conf.kind === "info" ? colors.primary[200] : colors.neutral[200]}`,
    }}>
      <LayerdIcon icon={conf.Icon} size={12} color="inherit" />
      {conf.label}
    </Stack>
  );
}

export default function ProposalActionButtons({
  proposal,
  onAct,
  canConfigure,
  busy,
  sx,
}) {
  if (!proposal) return null;

  const isResolved = proposal.state === "accepted"
    || proposal.state === "rejected"
    || proposal.state === "deferred";
  const isBusy = busy === proposal.id;
  const canReopen = (proposal.state === "accepted" && !proposal.promoted_rule_id)
    || proposal.state === "rejected"
    || proposal.state === "deferred";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      flexShrink={0}
      flexWrap="wrap"
      useFlexGap
      sx={sx}
    >
      {!isResolved && (
        <>
          <Button
            size="small"
            disableElevation
            startIcon={<LayerdIcon icon={Check} size="sm" color="inherit" />}
            disabled={!canConfigure || isBusy}
            sx={asecBtnPrimaryIconSm}
            onClick={() => onAct(proposal, "accept")}
          >
            Accept
          </Button>
          <Button
            size="small"
            disableElevation
            disabled={!canConfigure || isBusy}
            startIcon={<LayerdIcon icon={Clock} size="sm" color="inherit" />}
            sx={asecBtnOutlinedIconSm}
            onClick={() => onAct(proposal, "defer")}
          >
            Defer
          </Button>
          <Button
            size="small"
            disableElevation
            disabled={!canConfigure || isBusy}
            startIcon={<LayerdIcon icon={X} size="sm" color="inherit" />}
            sx={asecBtnDangerOutlinedSm}
            onClick={() => onAct(proposal, "reject")}
          >
            Reject
          </Button>
        </>
      )}
      {isResolved && <ResolvedFlag state={proposal.state} />}
      {isResolved && canReopen && !proposal.promoted_rule_id && (
        <GhostButton
          size="small"
          disabled={!canConfigure || isBusy}
          startIcon={<LayerdIcon icon={RotateCcw} size="sm" color="neutral" />}
          onClick={() => onAct(proposal, "reopen")}
        >
          Reopen
        </GhostButton>
      )}
    </Stack>
  );
}

ProposalActionButtons.propTypes = {
  proposal: PropTypes.object,
  onAct: PropTypes.func.isRequired,
  canConfigure: PropTypes.bool,
  busy: PropTypes.string,
  sx: PropTypes.object,
};
